// src/components/auth/RequireAuth.tsx
import { Navigate, Outlet, useLocation } from "react-router-dom";

// src/hooks/useSession.ts
import { useQuery } from "@tanstack/react-query";

// src/lib/api.ts
import { QueryClient } from "@tanstack/react-query";
var ApiError = class extends Error {
  constructor(status, statusText, body, message) {
    super(message ?? deriveMessage(status, statusText, body));
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.name = "ApiError";
  }
};
function deriveMessage(status, statusText, body) {
  const fallback = `${status} ${statusText}`;
  if (body == null || typeof body !== "object") return fallback;
  const b = body;
  const detail = typeof b.detail === "string" ? b.detail : void 0;
  const errStr = typeof b.error === "string" ? b.error : void 0;
  const msgStr = typeof b.message === "string" ? b.message : void 0;
  const codeStr = typeof b.code === "string" ? b.code : void 0;
  const reason = detail ?? errStr ?? msgStr ?? codeStr;
  return reason ? `${fallback} \u2014 ${reason}` : fallback;
}
function mergeHeaders(base, caller) {
  const h = new Headers(base);
  if (caller) {
    new Headers(caller).forEach((value, key) => {
      h.set(key, value);
    });
  }
  return h;
}
async function apiFetch(path, options = {}) {
  const { json, headers, credentials: _ignored, ...rest } = options;
  const baseHeaders = {
    Accept: "application/json",
    ...json !== void 0 ? { "Content-Type": "application/json" } : {}
  };
  const init = {
    ...rest,
    headers: mergeHeaders(baseHeaders, headers),
    credentials: "include",
    ...json !== void 0 ? { body: JSON.stringify(json) } : {}
  };
  const res = await fetch(path, init);
  if (res.status === 204) {
    return null;
  }
  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const body = isJson ? await res.json().catch(() => null) : await res.text();
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, body);
  }
  return body;
}

// src/hooks/useSession.ts
var SESSION_QUERY_KEY = ["auth", "session"];
function useSession() {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiFetch("/api/v1/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 6e4
  });
  return {
    session: query.data ?? null,
    isLoading: query.isLoading,
    isAuthenticated: query.data != null,
    error: query.error,
    refetch: query.refetch
  };
}

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/ui/skeleton.tsx
import { jsx } from "react/jsx-runtime";
function Skeleton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("animate-pulse rounded-md bg-muted", className),
      ...props
    }
  );
}

// src/components/shell/ErrorState.tsx
import { AlertTriangle } from "lucide-react";

// src/components/ui/alert.tsx
import * as React from "react";
import { cva } from "class-variance-authority";
import { jsx as jsx2 } from "react/jsx-runtime";
var alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Alert = React.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    role: "alert",
    className: cn(alertVariants({ variant }), className),
    ...props
  }
));
Alert.displayName = "Alert";
var AlertTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "h5",
  {
    ref,
    className: cn("mb-1 font-medium leading-none tracking-tight", className),
    ...props
  }
));
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    className: cn("text-sm [&_p]:leading-relaxed", className),
    ...props
  }
));
AlertDescription.displayName = "AlertDescription";

// src/components/ui/button.tsx
import * as React2 from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx3 } from "react/jsx-runtime";
var buttonVariants = cva2(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React2.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx3(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/shell/ErrorState.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
function readableError(error) {
  if (!error) return void 0;
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return void 0;
}
function ErrorState({
  title = "Something went wrong",
  error,
  description,
  retry,
  retryLabel = "Try again"
}) {
  const message = readableError(error);
  return /* @__PURE__ */ jsxs(Alert, { variant: "destructive", children: [
    /* @__PURE__ */ jsx4(AlertTriangle, { className: "h-4 w-4" }),
    /* @__PURE__ */ jsx4(AlertTitle, { children: title }),
    /* @__PURE__ */ jsxs(AlertDescription, { className: "mt-2 space-y-3", children: [
      description ?? message ?? "Please try again or contact support.",
      retry && /* @__PURE__ */ jsx4("div", { children: /* @__PURE__ */ jsx4(Button, { size: "sm", variant: "outline", onClick: retry, children: retryLabel }) })
    ] })
  ] });
}

// src/components/auth/RequireAuth.tsx
import { Fragment, jsx as jsx5 } from "react/jsx-runtime";
function RequireAuth({
  children,
  redirectTo = "/login",
  loadingFallback,
  errorFallback
}) {
  const { isLoading, isAuthenticated, error } = useSession();
  const location = useLocation();
  if (isLoading) {
    return /* @__PURE__ */ jsx5(Fragment, { children: loadingFallback ?? /* @__PURE__ */ jsx5(Skeleton, { className: "h-screen w-full" }) });
  }
  if (error) {
    return /* @__PURE__ */ jsx5(Fragment, { children: errorFallback ? errorFallback(error) : /* @__PURE__ */ jsx5(ErrorState, { error, title: "Unable to verify session" }) });
  }
  if (!isAuthenticated) {
    return /* @__PURE__ */ jsx5(Navigate, { to: redirectTo, replace: true, state: { from: location } });
  }
  return /* @__PURE__ */ jsx5(Fragment, { children: children ?? /* @__PURE__ */ jsx5(Outlet, {}) });
}
export {
  RequireAuth
};
//# sourceMappingURL=RequireAuth.js.map